import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { formatDistanceToNow } from "date-fns"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a date for display (Australian locale)
 * @param date - Date value (Date, string, number, null, or undefined)
 * @param options - Optional formatting options
 * @returns Formatted date string or fallback value
 */
export function formatDate(
  date: Date | string | number | null | undefined,
  options?: {
    fallback?: string
    includeTime?: boolean
    timeZone?: string
  }
): string {
  if (!date) return options?.fallback ?? '-'

  const d = date instanceof Date ? date : new Date(date)

  if (isNaN(d.getTime())) return options?.fallback ?? '-'

  const formatOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: options?.timeZone ?? 'Australia/Sydney',
  }

  if (options?.includeTime) {
    formatOptions.hour = 'numeric'
    formatOptions.minute = '2-digit'
  }

  return d.toLocaleDateString('en-AU', formatOptions)
}

/**
 * Format a date with time for display (Australian locale)
 * Convenience wrapper around formatDate with includeTime: true
 */
export function formatDateTime(
  date: Date | string | number | null | undefined,
  options?: { fallback?: string; timeZone?: string }
): string {
  return formatDate(date, { ...options, includeTime: true })
}

/**
 * Format a number as currency using Intl.NumberFormat
 * @param value - The numeric value to format
 * @param currency - The currency code (e.g., 'AUD', 'USD')
 * @param options - Optional formatting options
 * @returns Formatted currency string
 */
export function formatCurrency(
  value: number,
  currency: string = 'AUD',
  options?: {
    minimumFractionDigits?: number
    maximumFractionDigits?: number
    compact?: boolean
  }
): string {
  // Compact formatting for large numbers ($1.2M, $50K)
  if (options?.compact) {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`
    }
    return `$${value.toFixed(0)}`
  }

  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }).format(value)
}

/**
 * Format a unit for display (e.g., 'hour' → '/hr')
 * Used in product pricing and line items
 */
export function formatUnit(unit: string): string {
  const unitLabels: Record<string, string> = {
    each: '',
    hour: '/hr',
    day: '/day',
    week: '/wk',
    month: '/mo',
    year: '/yr',
    project: '/project',
  }
  return unitLabels[unit] ?? ''
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 * Uses date-fns formatDistanceToNow under the hood
 */
export function formatRelativeTime(date: Date | string | number | null | undefined): string {
  if (!date) return ''

  const d = date instanceof Date ? date : new Date(date)

  if (isNaN(d.getTime())) return ''

  return formatDistanceToNow(d, { addSuffix: true })
}

/**
 * Get initials from a name (max 2 characters)
 * @param name - Full name (e.g., "John Doe")
 * @param fallback - Optional fallback string if name is empty (uses first char)
 * @returns Uppercase initials (e.g., "JD")
 */
export function getInitials(name?: string | null, fallback?: string): string {
  if (!name || !name.trim()) {
    return fallback?.[0]?.toUpperCase() ?? 'U'
  }
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}
